/** @jsx jsx */
import { jsx } from '@emotion/core'
import React, { useState, useEffect, useMemo, useCallback } from 'react'
import {
  // Table,
  Pagination,
  Button,
} from 'antd'
// import { Resizable } from 'react-resizable'
import { Filler } from 'core/screen-util'
import R from 'ramda'

import ColumnSelectModal from './columnSelectModal'
import CalcModal from './calcModal'
import FilterSortModal from './filterSortModal'
import ChartModal from './chartModal'
import {
  getAggregationData,
  visibleColumns,
  toXlsxColumns,
  columnToGridHeaders,
  getColorFromColorInfoMap,
  getColorInfoMapFromList,
} from './utils'
import { XlsxExport } from 'core/component'
import {
  virtualizedGridRenderer,
  defaultVirtualizedCellRender,
} from '@integec/grid-tools'
import { resizableHeaderRender } from 'core/utils'
import Grid from '@integec/grid-tools/lib/Grid'
import { useWorker } from 'workers'
import ReportMetaModal from './reportMetaModal'
import {
  AND_PASS,
  defaultFilterData,
  generateSortFunction,
} from 'grid-filter/GridFilter'

const gridCellRender = colorInfoMap => props => {
  const { rowIndex, columnIndex } = props.reactVirtualizedProps
  const { data, headers } = props.gridToolProps
  const rowData = data[rowIndex]
  // const header = headers[columnIndex]

  if (rowData.aggRow) {
    return defaultVirtualizedCellRender({
      ...props,
      backgroundColor: 'lightyellow',
    })
  }

  const derivedBgColor = getColorFromColorInfoMap(
    rowData.row,
    headers[columnIndex].ident,
    colorInfoMap
  )

  if (derivedBgColor != null) {
    return defaultVirtualizedCellRender({
      ...props,
      backgroundColor: '#' + derivedBgColor,
    })
  }

  return defaultVirtualizedCellRender(props)
}

const ReportTable = ({
  table,
  effectiveData,
  setEffectiveData,
  profile = {},
  setProfile,
  tableCount,
  totalHeight,
  totalWidth,
  maximizedTable,
  setMaximizedTable,
}) => {
  const { columnHeaders, colorInfo, data: allData } = table

  const [filterCondition, setFilterCondition] = useState([
    { andOrPass: AND_PASS },
  ])
  const [andOrPass, setAndOrPass] = useState(AND_PASS)

  useEffect(
    _ => {
      try {
        if (!profile.filterText) {
          setFilterCondition([{ andOrPass: AND_PASS }])
        } else {
          const filterComp = JSON.parse(profile.filterText || '[]')
          setAndOrPass((filterComp[0] || {}).andOrPass || AND_PASS)
          setFilterCondition(filterComp)
        }
      } catch (error) {
        setFilterCondition([{ andOrPass: AND_PASS }])
      }
    },
    [profile.filterText]
  )

  const colorInfoMap = useMemo(() => getColorInfoMapFromList(colorInfo), [
    colorInfo,
  ])

  const heightOfComp = useMemo(() => {
    const contentHeight = totalHeight - 30
    return tableCount > 2 ? contentHeight / 3 : contentHeight / tableCount
  }, [tableCount, totalHeight])

  // const sortOrderArray = useMemo(
  //   () =>
  //     (profile.sortText &&
  //       profile.sortText.split(',').map(item => {
  //         const idOrderArray = item.split(' ')
  //         return {
  //           ...(columnHeaders.find(c => c.id === idOrderArray[0]) || {
  //             id: idOrderArray[0],
  //           }),
  //           order: idOrderArray[1],
  //         }
  //       })) ||
  //     [],
  //   [columnHeaders, profile.sortText]
  // )

  const [pageStartEnd, setPageStartEnd] = useState([0, 1000])

  const [columnState, setColumnState] = useState([])

  useEffect(() => {
    setColumnState(columnToGridHeaders(columnHeaders))
  }, [columnHeaders])

  const [showColumnSelect, setShowColumnSelect] = useState(false)
  const [showCalc, setShowCalc] = useState(false)
  const [showFilterSort, setShowFilterSort] = useState(false)
  const [showChart, setShowChart] = useState(false)
  const [showMetaInfo, setShowMetaInfo] = useState(false)

  const aggregationData = useMemo(() => {
    return profile.calculations
      ? getAggregationData(
          effectiveData,
          columnHeaders,
          profile.calculations.split(',')
        )
      : []
  }, [columnHeaders, effectiveData, profile.calculations])

  useEffect(() => {
    setColumnState(currCols =>
      columnToGridHeaders(visibleColumns(profile, columnHeaders)).map(
        header => currCols.find(col => col.ident === header.ident) || header
      )
    )

    let processedData = []

    try {
      processedData = defaultFilterData(
        allData,
        columnHeaders,
        filterCondition,
        andOrPass
      )
      let sortItems = []
      try {
        sortItems = JSON.parse(profile.sortText)
      } catch (error) {
        sortItems = []
      }

      const sortFunc = generateSortFunction(sortItems || [])
      processedData = sortFunc(processedData)
    } catch (e) {
      console.log('error while executing query string', e)
    }

    if (!R.equals(effectiveData, processedData)) setEffectiveData(processedData)
  }, [
    columnHeaders,
    profile,
    // sortOrderArray,
    allData,
    effectiveData,
    setEffectiveData,
    filterCondition,
    andOrPass,
  ])

  const onPageChange = useCallback((currentPage, pageSize) => {
    const pageStart = currentPage > 0 ? (currentPage - 1) * +pageSize : 0
    const pageEnd = +pageStart + +pageSize
    setPageStartEnd([pageStart, pageEnd])
  }, [])

  const handleResize = useCallback(
    index => (e, { size }) => {
      setColumnState(columns => {
        const nextColumns = [...columns]
        nextColumns[index] = {
          ...nextColumns[index],
          width: size.width,
        }
        return nextColumns
      })
    },
    []
  )

  const setProfileCall = p => {
    setFilterCondition([{}])
    setProfile(p)
  }

  const [utilWorker] = useWorker({
    getWorkerInstance: () =>
      new Worker('workers/rpt-dash-worker.js', { type: 'module' }),
  })

  return (
    <div
      css={{
        marginTop: 5,
        marginBottom: 2,
        borderBottom: 'solid lightgrey 1px',
        height: heightOfComp,
        display:
          maximizedTable == null || maximizedTable === table.tableName
            ? ''
            : 'none',
      }}
    >
      {showColumnSelect && (
        <ColumnSelectModal
          profile={profile}
          setProfile={setProfileCall}
          columnHeaders={columnHeaders}
          hide={() => setShowColumnSelect(false)}
        />
      )}
      {showCalc && (
        <CalcModal
          profile={profile}
          setProfile={setProfileCall}
          hide={() => setShowCalc(false)}
        />
      )}
      {showFilterSort && (
        <FilterSortModal
          columnHeaders={columnHeaders}
          // sortOrderArray={sortOrderArray}
          profile={profile}
          setProfile={setProfileCall}
          hide={() => setShowFilterSort(false)}
          andOrPass={andOrPass}
          setAndOrPass={setAndOrPass}
          filterCondition={filterCondition}
          setFilterCondition={setFilterCondition}
        />
      )}
      {showChart && (
        <ChartModal
          columns={columnState}
          data={effectiveData}
          profile={profile}
          setProfile={setProfileCall}
          hide={() => setShowChart(false)}
        />
      )}
      {showMetaInfo && (
        <ReportMetaModal
          metaInfo={table.metaInfo}
          hide={() => setShowMetaInfo(false)}
        />
      )}
      <div css={{ display: 'flex', marginBottom: 2 }}>
        <Button size="small" onClick={() => setShowFilterSort(true)}>
          Filter / Sort
        </Button>
        <Button
          size="small"
          css={{ marginLeft: 10 }}
          onClick={() => setShowColumnSelect(true)}
        >
          Select Column
        </Button>
        <Button
          size="small"
          css={{ marginLeft: 10 }}
          onClick={() => setShowCalc(true)}
        >
          Show Calc.
        </Button>
        <XlsxExport
          size="small"
          btnStyle={{ marginLeft: 10 }}
          fileName={table.displayName}
          getWorksheets={async () => [
            {
              name: table.displayName,
              columns: toXlsxColumns(visibleColumns(profile, columnHeaders)),
              data: await utilWorker.toXlsxData(
                effectiveData.concat(aggregationData),
                visibleColumns(profile, columnHeaders)
              ),
              colorInfo: colorInfo,
              metaInfo: table.metaInfo,
            },
          ]}
        />
        <Button
          size="small"
          css={{ marginLeft: 10 }}
          type={maximizedTable == null ? 'primary' : 'default'}
          icon={maximizedTable == null ? 'plus' : 'minus'}
          onClick={() => {
            setMaximizedTable(active =>
              active == null ? table.tableName : null
            )
          }}
        />

        <Button
          size="small"
          css={{ marginLeft: 10 }}
          type="primary"
          icon="area-chart"
          onClick={() => {
            setShowChart(true)
          }}
        />
        <Button
          size="small"
          css={{ marginLeft: 10 }}
          type="primary"
          icon="info-circle"
          onClick={() => {
            setShowMetaInfo(true)
          }}
        />
        <Filler
          css={{
            fontSize: 15,
            fontWeight: 'bold',
            color: 'blue',
            textAlign: 'center',
          }}
        >
          {table.displayName}
        </Filler>
        {/* <div>{`Total ${effectiveData.length} Records`}</div> */}
        <Pagination
          size="small"
          total={effectiveData.length}
          defaultPageSize="1000"
          pageSizeOptions={['100', '500', '1000']}
          showSizeChanger
          showQuickJumper
          showTotal={total =>
            `${pageStartEnd[0]}-${pageStartEnd[1]} of ${total} records`
          }
          onChange={onPageChange}
          onShowSizeChange={onPageChange}
        />
      </div>
      <Grid
        isEditable={false}
        sortEnabled={false}
        data={effectiveData
          .slice(pageStartEnd[0], pageStartEnd[1])
          .concat(aggregationData)}
        headers={columnState}
        render={virtualizedGridRenderer({
          rowHeaderRender: resizableHeaderRender(handleResize),
          headerRowHeight: 30,
          cellRender: gridCellRender(colorInfoMap),
          width: totalWidth - 20,
          height: heightOfComp - 30,
        })}
      />
      {/* <Table
        bordered
        components={{
          header: {
            cell: ResizableTitle,
          },
        }}
        columns={columns}
        dataSource={effectiveData
          .slice(pageStartEnd[0], pageStartEnd[1])
          .concat(aggregationData)}
        pagination={false}
        scroll={{
          x: columns.map(c => c.title.length).reduce((t, c) => t + c) * 14,
          y: heightOfComp - 70,
        }}
        onRow={(record, _index) => {
          if (record.aggRow) {
            return {
              style: {
                background: 'lightyellow',
              },
            }
          }

          return {}
        }}
      /> */}
    </div>
  )
}

export default ReportTable
